import { useMemo, useState } from "react";
import {
  CSV_TARGETS,
  autoMatchColumns,
  buildPointsFc,
  type CsvTarget,
  type ParsedCsv,
} from "../lib/csv";

// Check/re-match step for a CSV point-sample upload: columns are AUTO-MATCHED to attributes, every
// match is shown as a select the user can change (with a sample value from the file so a wrong
// match is obvious), then Save. Lat/Long are required; everything else is optional.
export function CsvPointsMapper({
  parsed,
  onSave,
  onSaveMapping,
  onCancel,
  saveLabel = "Save points", // the upload-AND-ORDER flow relabels ("Use these points")
  targets = CSV_TARGETS,
  autoMatch = autoMatchColumns,
}: {
  parsed: ParsedCsv;
  onSave?: (result: { fc: unknown; saved: number; skipped: number }) => void;
  onSaveMapping?: (
    mapping: Record<string, number | null>,
    parsed: ParsedCsv,
  ) => void;
  onCancel: () => void;
  saveLabel?: string;
  targets?: CsvTarget[];
  autoMatch?: (parsed: ParsedCsv) => Record<string, number | null>;
}) {
  const [mapping, setMapping] = useState<Record<string, number | null>>(() =>
    autoMatch(parsed),
  );

  // first non-empty value per column — the "is this match right?" hint
  const sampleOf = useMemo(() => {
    return (col: number | null): string => {
      if (col == null) return "";
      for (const r of parsed.rows.slice(0, 25)) {
        const v = (r[col] ?? "").trim();
        if (v) return v;
      }
      return "";
    };
  }, [parsed]);

  const latLonReady = mapping.lat != null && mapping.lon != null;
  const matchedCount = targets.filter((t) => mapping[t.key] != null).length;
  const save = () =>
    onSaveMapping
      ? onSaveMapping(mapping, parsed)
      : onSave?.(buildPointsFc(parsed, mapping));

  return (
    <div className="rounded border border-line bg-canvas/60 p-2">
      <p className="mb-1 text-[11px] font-medium">
        {parsed.rows.length.toLocaleString()} rows parsed — check the column
        matching ({matchedCount} auto-matched):
      </p>
      {parsed.note && (
        <p role="status" className="mb-1 text-[11px] text-fg">
          {parsed.note}
        </p>
      )}
      <div className="grid max-h-56 grid-cols-1 gap-x-4 gap-y-1 overflow-auto sm:grid-cols-2">
        {targets.map((t) => {
          const col = mapping[t.key];
          const missing = t.required && col == null;
          return (
            <label
              key={t.key}
              className="flex items-center justify-between gap-2 text-[10px]"
            >
              <span
                className={`shrink-0 ${missing ? "text-red-400" : "text-muted"}`}
              >
                {t.label}
                {t.required && " *"}
              </span>
              <span className="flex min-w-0 items-center gap-1">
                {col != null && (
                  <span
                    className="truncate text-[9px] text-faint"
                    title="sample value"
                  >
                    e.g. {sampleOf(col)}
                  </span>
                )}
                <select
                  value={col ?? -1}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setMapping((m) => ({ ...m, [t.key]: v === -1 ? null : v }));
                  }}
                  aria-label={`Column for ${t.label}`}
                  className={`w-32 rounded border bg-surface px-1 py-0.5 text-[10px] text-fg outline-none ${missing ? "border-red-400/60" : "border-line"}`}
                >
                  <option value={-1}>— skip —</option>
                  {parsed.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `(column ${i + 1})`}
                    </option>
                  ))}
                </select>
              </span>
            </label>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          disabled={!latLonReady}
          className="rounded bg-accent px-2.5 py-1 text-xs font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
          onClick={save}
        >
          {saveLabel}
        </button>
        <button
          className="rounded border border-line px-2 py-1 text-xs text-fg hover:bg-hover"
          onClick={onCancel}
        >
          Cancel
        </button>
        {!latLonReady && (
          <span className="text-[10px] text-red-400">
            match the Latitude and Longitude columns to continue
          </span>
        )}
      </div>
    </div>
  );
}
